/**
 * Phase 21 wizard — bathroom project-type step.
 *
 * Shown only when the estimate's room type has active packages. One tap
 * seeds the whole itemized estimate, replacing the per-category grind
 * that made bathrooms unquotable.
 *
 * Unlike the other steps this one does NOT fire a Stella turn — applying
 * a package is a deterministic bulk write — so the host awaits the
 * mutation and then advances. The "Something else" card applies nothing
 * and hands off to Stella instead.
 */

import type { PackageCard } from "@starz/shared/api/types.gen";
import { OTHER_PROJECT_TYPE_ID } from "@starz/shared/wizard";

interface Props {
  /** Project-type choices for this estimate (already room-scoped by the API). */
  packages: PackageCard[];
  /** Which package is applied, if any — so a returning customer sees it. */
  appliedPackageId: string | null;
  /** Apply a package, or hand off when `OTHER_PROJECT_TYPE_ID`. */
  onSelect: (packageId: string) => void;
  /** An apply is in flight — block double-taps. */
  busy?: boolean;
  /** Customer-facing message when the last apply failed. */
  errorMessage?: string | null;
  disabled?: boolean;
}

export function ProjectTypeStep({
  packages,
  appliedPackageId,
  onSelect,
  busy = false,
  errorMessage = null,
  disabled = false,
}: Props) {
  const locked = disabled || busy;

  return (
    <div className="space-y-4">
      <p className="text-sm text-warm-gray">
        Pick the option closest to your project and we'll build a full estimate you
        can adjust. Not sure? Choose <span className="whitespace-nowrap">Something else</span>{" "}
        and Stella will help.
      </p>

      {errorMessage && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {errorMessage}
        </p>
      )}

      <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {packages.map((p) => {
          const isSelected = p.package_id === appliedPackageId;
          return (
            <li key={p.package_id}>
              <button
                type="button"
                onClick={() => onSelect(p.package_id)}
                disabled={locked}
                aria-pressed={isSelected}
                className={
                  "flex min-h-[44px] w-full flex-col gap-2 rounded-xl border-2 bg-white p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 " +
                  (isSelected
                    ? "border-starz-gold ring-2 ring-starz-gold/40"
                    : "border-starz-gold/15 hover:border-starz-gold/40")
                }
              >
                {p.photo_url ? (
                  <img
                    src={p.photo_url}
                    alt={p.display_name}
                    loading="lazy"
                    className="aspect-[4/3] w-full rounded-md object-cover"
                  />
                ) : (
                  <div className="grid aspect-[4/3] w-full place-items-center rounded-md bg-starz-cream text-xs text-warm-gray">
                    No photo yet
                  </div>
                )}
                <div>
                  <div className="text-base font-medium text-starz-navy">
                    {p.display_name}
                  </div>
                  {p.description && (
                    <div className="line-clamp-2 text-xs text-warm-gray">
                      {p.description}
                    </div>
                  )}
                </div>
                {isSelected && (
                  <span className="text-xs font-semibold uppercase tracking-widest text-starz-gold">
                    Selected
                  </span>
                )}
              </button>
            </li>
          );
        })}

        {/* The catch-all. Applies no package — genuinely bespoke work goes
            to Stella rather than getting squeezed into a bundle. */}
        <li key={OTHER_PROJECT_TYPE_ID}>
          <button
            type="button"
            onClick={() => onSelect(OTHER_PROJECT_TYPE_ID)}
            disabled={locked}
            className="flex min-h-[44px] w-full flex-col gap-2 rounded-xl border-2 border-dashed border-starz-gold/30 bg-starz-cream/30 p-3 text-left transition-colors hover:border-starz-gold/60 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <div className="grid aspect-[4/3] w-full place-items-center rounded-md bg-white/60 text-2xl text-starz-gold">
              ?
            </div>
            <div>
              <div className="text-base font-medium text-starz-navy">Something else</div>
              <div className="text-xs text-warm-gray">
                Describe your project to Stella instead.
              </div>
            </div>
          </button>
        </li>
      </ul>

      {/* FR-009a — say that applying sets their selections, so the
          overwrite is never a surprise. */}
      <p className="text-xs text-warm-gray">
        {appliedPackageId
          ? "Choosing a different option replaces the items from your current one. Anything you picked yourself outside those categories stays."
          : "We'll add the items for the option you pick — you can change any of them afterwards."}
      </p>

      {busy && (
        <p role="status" className="text-sm text-warm-gray">
          Building your estimate…
        </p>
      )}
    </div>
  );
}
